import { forwardRef, useState, memo, useMemo } from 'react';

import * as Recharts from 'recharts';

import { Legend, LegendContent } from '../Legend/index';
import PieTooltipContent from './PieTooltipContent';
import { usePieElements } from './hooks';

const getSize = (width) => {
  if (width < 410) {
    return 'sm';
  } else if (width < 800) {
    return 'md';
  }
  return 'lg';
};

const PieChart = forwardRef(
  (
    {
      data,
      metrics,
      colorGetter,
      compareMode,

      unknown,
      chartState,
      series,
      onToggleMetric,
      legend,
      labelType,
      ...props
    },
    ref
  ) => {
    const { width: containerWidth } = props;
    let width = containerWidth;
    if (legend && compareMode) {
      width = width * 0.7;
    }

    const [hoveredMetric, setHoveredMetric] = useState(null);
    const hiddenMetrics = chartState.hiddenMetrics;

    const metricSum = useMemo(() => {
      const cols = metrics
        .filter(({ id }) => !hiddenMetrics.includes(id))
        .map(({ dataKey }) => dataKey);

      let _sum = cols.reduce((initial, col) => ({ ...initial, [col]: 0 }), {});
      data.forEach((row) => {
        cols.forEach((col) => {
          _sum[col] += row[col] || 0;
        });
      });

      cols.forEach((col) => {
        _sum[col] = parseFloat(_sum[col].toFixed(2));
      });

      return _sum;
    }, [data, metrics, hiddenMetrics]);

    const pieElements = usePieElements({
      metrics,
      colorGetter,
      metricSum: metricSum,
      hiddenMetrics: hiddenMetrics,
      hoveredMetric: hoveredMetric,
      onHover: setHoveredMetric,
      legend: legend,
      size: getSize(width),
      labelType: labelType,
    });

    return (
      <Recharts.PieChart {...props} ref={ref} data-cy-chart-type={'pie'}>
        {pieElements}
        {legend && (
          <Legend
            content={
              <LegendContent
                onToggle={onToggleMetric}
                onHover={setHoveredMetric}
                hiddenMetrics={hiddenMetrics}
              />
            }
          />
        )}
        <Recharts.Tooltip
          offset={100}
          content={<PieTooltipContent labelType={labelType} />}
        />
      </Recharts.PieChart>
    );
  }
);

const propsArray = [
  'height',
  'width',
  'data',
  'metrics',
  'colorGetter',
  'compareMode',
  'margin',
  'chartState',
  'legend',
  'onToggleMetric',
  'labelType',
];

function areEqual(prevProps, nextProps) {
  return propsArray.every((prop) => {
    const equal = prevProps[prop] === nextProps[prop];
    return equal;
  });
}

export default memo(PieChart, areEqual);
